import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiAPI, storybookAPI } from '../utils/api';
import { ReadingAge } from '../types/storybook';
import { createSeriesStyleDirection } from '@tiny-fox/storybook-layout';
import {
  approveQuickCreateStage,
  createInitialQuickCreateState,
  estimateQuickCreate,
  formatQuickCreateDuration,
  READING_AGE_PRESETS
} from '../utils/quickCreate';
import ReferenceBookSelector from './ReferenceBookSelector';
import './StorybookWizard.css';

interface WizardProps {
  onClose: () => void;
  onComplete: (storybookId: string) => void;
}

interface StorybookData {
  title: string;
  plot: string;
  readingAge: ReadingAge;
  canvasSize: string;
  pageCount: number;
  storyType: string;
  storyTone: string;
  drawingMedium: string;
  artStyle: string;
  renderingQuality: string;
  customStoryType?: string;
  customDrawingMedium?: string;
  customArtStyle?: string;
  referenceBook?: string;
  customBookName?: string;
  storyStylePrompt?: string;
  visualStylePrompt?: string;
  useReferenceBook: boolean;
  seriesTemplateId?: 'tiny-little-fox-sequel-v1';
}

const CANVAS_SIZES = [
  // Paperback Trim Sizes
  { value: '5x8', label: '5" × 8" (Paperback)', width: 500, height: 800 },
  { value: '5.06x7.81', label: '5.06" × 7.81" (Paperback)', width: 506, height: 781 },
  { value: '5.25x8', label: '5.25" × 8" (Paperback)', width: 525, height: 800 },
  { value: '5.5x8.5', label: '5.5" × 8.5" (Paperback)', width: 550, height: 850 },
  { value: '6x9', label: '6" × 9" (Paperback)', width: 600, height: 900 },
  
  // Large Paperback Trim Sizes
  { value: '6.14x9.21', label: '6.14" × 9.21" (Paperback)', width: 614, height: 921 },
  { value: '6.69x9.61', label: '6.69" × 9.61" (Paperback)', width: 669, height: 961 },
  { value: '7x10', label: '7" × 10" (Paperback)', width: 700, height: 1000 },
  { value: '7.44x9.69', label: '7.44" × 9.69" (Paperback)', width: 744, height: 969 },
  { value: '7.5x9.25', label: '7.5" × 9.25" (Paperback)', width: 750, height: 925 },
  { value: '8x10', label: '8" × 10" (Paperback)', width: 800, height: 1000 },
  { value: '8.25x6', label: '8.25" × 6" (Paperback)', width: 825, height: 600 },
  { value: '8.25x8.25', label: '8.25" × 8.25" (Paperback)', width: 825, height: 825 },
  { value: '8.5x8.5', label: '8.5" × 8.5" (Paperback)', width: 850, height: 850 },
  { value: '8.5x11', label: '8.5" × 11" (Paperback)', width: 850, height: 1100 },
  { value: '8.27x11.69', label: '8.27" × 11.69" (Paperback)', width: 827, height: 1169 },
  
  // Hardcover Trim Sizes
  { value: '5.5x8.5-hc', label: '5.5" × 8.5" (Hardcover)', width: 550, height: 850 },
  { value: '6x9-hc', label: '6" × 9" (Hardcover)', width: 600, height: 900 },
  { value: '6.14x9.21-hc', label: '6.14" × 9.21" (Hardcover)', width: 614, height: 921 },
  { value: '7x10-hc', label: '7" × 10" (Hardcover)', width: 700, height: 1000 },
  { value: '8.25x11-hc', label: '8.25" × 11" (Hardcover)', width: 825, height: 1100 }
];

const STORY_TYPES = [
  'Fairy Tale',
  'Adventure',
  'Mystery',
  'Fantasy',
  'Science Fiction',
  'Animal Story',
  'Educational',
  'Bedtime Story',
  'Custom'
];

const STORY_TONES = [
  'Playful',
  'Whimsical',
  'Adventurous',
  'Mysterious',
  'Heartwarming',
  'Educational',
  'Funny',
  'Calm'
];

const StorybookWizard: React.FC<WizardProps> = ({ onClose, onComplete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [referenceBusy, setReferenceBusy] = useState(false);
  const [error, setError] = useState('');

  const [data, setData] = useState<StorybookData>({
    title: '',
    plot: '',
    readingAge: '6-8',
    canvasSize: READING_AGE_PRESETS['6-8'].canvasSize,
    pageCount: READING_AGE_PRESETS['6-8'].pageCount,
    storyType: 'Fairy Tale',
    storyTone: READING_AGE_PRESETS['6-8'].storyTone,
    drawingMedium: 'Digital Art',
    artStyle: 'Children\'s Book Illustration',
    renderingQuality: 'Detailed',
    customStoryType: '',
    customDrawingMedium: '',
    customArtStyle: '',
    referenceBook: '',
    customBookName: '',
    storyStylePrompt: '',
    visualStylePrompt: '',
    useReferenceBook: false
  });

  const provisionalWorkflow = useMemo(() => createInitialQuickCreateState({
    premise: data.plot.trim() || 'Story premise to be completed',
    readingAge: data.readingAge,
    pageCount: data.pageCount,
    canvasSize: data.canvasSize,
    storyType: data.storyType === 'Custom' ? (data.customStoryType || 'Custom') : data.storyType,
    storyTone: data.storyTone
  }), [data.canvasSize, data.customStoryType, data.pageCount, data.plot, data.readingAge, data.storyTone, data.storyType]);
  const provisionalEstimate = useMemo(
    () => estimateQuickCreate(provisionalWorkflow),
    [provisionalWorkflow]
  );

  const handleReadingAgeChange = (readingAge: ReadingAge) => {
    const preset = READING_AGE_PRESETS[readingAge];
    setData(current => ({
      ...current,
      readingAge,
      pageCount: preset.pageCount,
      canvasSize: preset.canvasSize,
      storyTone: preset.storyTone
    }));
  };

  const handleAnalyzeReferenceBook = async (bookName: string) => {
    setReferenceBusy(true);
    setError('');
    try {
      const response = await aiAPI.generateBookPrompts(bookName);
      if (!response?.success || !response.data?.storyStylePrompt || !response.data?.visualStylePrompt) {
        throw new Error('The reference analysis returned no usable style direction.');
      }
      setData(current => ({
        ...current,
        useReferenceBook: true,
        referenceBook: bookName,
        seriesTemplateId: undefined,
        storyStylePrompt: response.data.storyStylePrompt,
        visualStylePrompt: response.data.visualStylePrompt
      }));
    } catch (analysisError: any) {
      setError(analysisError?.response?.data?.error || analysisError?.message || 'Failed to analyze the reference book.');
    } finally {
      setReferenceBusy(false);
    }
  };

  const handleUseSeriesTemplate = (templateId: 'tiny-little-fox-sequel-v1') => {
    const direction = createSeriesStyleDirection(templateId);
    setData(current => ({
      ...current,
      useReferenceBook: true,
      referenceBook: direction.referenceBook,
      seriesTemplateId: direction.seriesTemplateId,
      readingAge: direction.readingAge,
      canvasSize: direction.canvasSize,
      storyStylePrompt: direction.storyStylePrompt,
      visualStylePrompt: direction.visualStylePrompt
    }));
  };

  const handleClearReferenceBook = () => {
    setData(current => ({
      ...current,
      useReferenceBook: false,
      referenceBook: '',
      seriesTemplateId: undefined,
      storyStylePrompt: '',
      visualStylePrompt: ''
    }));
  };

  const handleCreate = async () => {
    if (!data.title.trim()) {
      setError('Please enter a title');
      return;
    }
    if (!data.plot.trim()) {
      setError('Please describe the story you want to create');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const canvasInfo = CANVAS_SIZES.find(c => c.value === data.canvasSize);
      
      const drawingMedium = data.drawingMedium === 'Custom' ? data.customDrawingMedium : data.drawingMedium;
      const artStyle = data.artStyle === 'Custom' ? data.customArtStyle : data.artStyle;
      
      const now = new Date().toISOString();
      const initialWorkflow = createInitialQuickCreateState({
        premise: data.plot.trim(),
        readingAge: data.readingAge,
        pageCount: data.pageCount,
        canvasSize: data.canvasSize,
        storyType: data.storyType === 'Custom' ? (data.customStoryType || 'Custom') : data.storyType,
        storyTone: data.storyTone
      }, now);
      const approvedBrief = approveQuickCreateStage(initialWorkflow, 'brief', 'outline', now);
      const quickCreate = {
        ...approvedBrief,
        estimate: {
          tokens: provisionalEstimate.tokens,
          seconds: provisionalEstimate.seconds,
          basis: 'provisional' as const,
          calculatedAt: now
        }
      };
      
      const createData = {
        title: data.title,
        description: data.plot.trim(),
        plot: data.plot.trim(),
        canvasSize: data.canvasSize,
        canvasWidth: canvasInfo?.width || 800,
        canvasHeight: canvasInfo?.height || 500,
        pageCount: data.pageCount,
        storyType: data.storyType === 'Custom' ? data.customStoryType : data.storyType,
        storyTone: data.storyTone,
        drawingType: drawingMedium,
        drawingStyle: artStyle,
        renderingQuality: data.renderingQuality,
        useReferenceBook: data.useReferenceBook,
        referenceBook: data.referenceBook,
        storyStylePrompt: data.storyStylePrompt,
        visualStylePrompt: data.visualStylePrompt,
        seriesTemplateId: data.seriesTemplateId,
        quickCreate
      };
      
      const response = await storybookAPI.create(createData);

      onComplete(response.storybook._id);
      navigate(`/editor/${response.storybook._id}?quickCreate=true`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create storybook');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wizard-overlay">
      <div className="wizard-modal">
        <div className="wizard-header">
          <h2>✨ Create New Storybook</h2>
          <button className="wizard-close" onClick={onClose} disabled={loading || referenceBusy}>
            ×
          </button>
        </div>

        <div className="wizard-body">
          {error && (
            <div className="wizard-error">
              {error}
            </div>
          )}

          <div className="wizard-step">
            <h3>Start with a clear brief</h3>
            <p className="wizard-description">Quick Create will guide this book through outline, art, draft, review, and PDF export.</p>
            
            <div className="form-group">
              <label htmlFor="title" className="form-label">Story Title *</label>
              <input
                id="title"
                type="text"
                className="form-input"
                value={data.title}
                onChange={(e) => setData({ ...data, title: e.target.value })}
                placeholder="The Adventures of Little Fox"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="plot" className="form-label">What should the story be about? *</label>
              <textarea
                id="plot"
                className="form-textarea"
                value={data.plot}
                onChange={(e) => setData({ ...data, plot: e.target.value })}
                placeholder="Describe what your story is about..."
                rows={4}
              />
              <small className="form-hint">Include the main character, goal, challenge, and the feeling you want at the end.</small>
            </div>

            <ReferenceBookSelector
              currentBook={data.useReferenceBook ? data.referenceBook : undefined}
              currentTemplateId={data.seriesTemplateId}
              storyStylePrompt={data.storyStylePrompt}
              visualStylePrompt={data.visualStylePrompt}
              busy={referenceBusy}
              disabled={loading}
              onAnalyze={handleAnalyzeReferenceBook}
              onUseSeriesTemplate={handleUseSeriesTemplate}
              onClear={handleClearReferenceBook}
            />

            <fieldset className="wizard-age-group">
              <legend>Reading age</legend>
              <div className="wizard-age-options">
                {(Object.keys(READING_AGE_PRESETS) as ReadingAge[]).map(readingAge => {
                  const preset = READING_AGE_PRESETS[readingAge];
                  return (
                    <label key={readingAge} className={`wizard-age-option${data.readingAge === readingAge ? ' selected' : ''}`}>
                      <input
                        type="radio"
                        name="readingAge"
                        value={readingAge}
                        checked={data.readingAge === readingAge}
                        onChange={() => handleReadingAgeChange(readingAge)}
                      />
                      <span><strong>{preset.label}</strong><small>{preset.description}</small></span>
                    </label>
                  );
                })}
              </div>
              <small className="form-hint">Changing age applies recommended pages, trim, and tone. You can adjust them below.</small>
            </fieldset>

            <div className="form-group">
              <label htmlFor="canvasSize" className="form-label">Canvas Size</label>
              <select
                id="canvasSize"
                className="form-select"
                value={data.canvasSize}
                onChange={(e) => setData({ ...data, canvasSize: e.target.value })}
              >
                {CANVAS_SIZES.map(size => (
                  <option key={size.value} value={size.value}>
                    {size.label} - {size.width}×{size.height}px
                  </option>
                ))}
              </select>
            </div>

            <div className="wizard-form-grid">
              <div className="form-group">
                <label htmlFor="storyType" className="form-label">Story type</label>
                <select
                  id="storyType"
                  className="form-select"
                  value={data.storyType}
                  onChange={(e) => setData({ ...data, storyType: e.target.value })}
                >
                  {STORY_TYPES.filter(type => type !== 'Custom').map(type => <option key={type}>{type}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="storyTone" className="form-label">Tone</label>
                <select
                  id="storyTone"
                  className="form-select"
                  value={data.storyTone}
                  onChange={(e) => setData({ ...data, storyTone: e.target.value })}
                >
                  {STORY_TONES.map(tone => <option key={tone}>{tone}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="pageCount" className="form-label">Number of Story Pages</label>
              <input
                id="pageCount"
                type="number"
                className="form-input"
                value={data.pageCount}
                onChange={(e) => setData({ ...data, pageCount: Math.max(1, Math.min(100, parseInt(e.target.value) || 1)) })}
                min="1"
                max="100"
              />
              <small className="form-hint">Front cover and back cover will be added automatically</small>
            </div>

            <div className="wizard-estimate" data-testid="quick-create-wizard-estimate">
              <span className="wizard-estimate-icon" aria-hidden="true">⌁</span>
              <div>
                <strong>Full first-draft estimate</strong>
                <p>{provisionalEstimate.tokens} tokens · {formatQuickCreateDuration(provisionalEstimate.seconds)}</p>
                <small>We will recalculate after your outline so shared cast and backgrounds are counted only once.</small>
              </div>
            </div>
          </div>
        </div>

        <div className="wizard-footer">
          <div className="wizard-actions-left">
            <button 
              className="wizard-btn wizard-btn-secondary" 
              onClick={onClose}
              disabled={loading || referenceBusy}
            >
              Cancel
            </button>
          </div>

          <div className="wizard-actions-right">
            <button 
              className="wizard-btn wizard-btn-success" 
              onClick={handleCreate}
              disabled={loading || referenceBusy || !data.title.trim() || !data.plot.trim()}
            >
              {loading ? 'Creating...' : 'Create & review outline →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorybookWizard;
